import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';
import { clearBrowserSession } from './browserSession';

const AuthContext = createContext(null);

const KEYS = { access: 'accessToken', refresh: 'refreshToken', user: 'user' };

// Read the saved session synchronously on the very first render. If user started as null and was
// only filled in by an effect, every guarded route would redirect to /login on refresh before the
// session was restored. The effect below still re-verifies it against the backend.
function readSavedUser() {
  try {
    if (!localStorage.getItem(KEYS.access)) return null;
    return JSON.parse(localStorage.getItem(KEYS.user));
  } catch {
    return null;
  }
}

/**
 * The one place that owns the signed-in user. Pages and guards read it with useAuth():
 *   const { user, isLoggedIn, signIn, signOut, updateUser } = useAuth();
 */
export function AuthProvider({ children }) {
  const [user, setUserState] = useState(readSavedUser);

  const updateUser = useCallback((next) => {
    localStorage.setItem(KEYS.user, JSON.stringify(next));
    setUserState(next);
  }, []);

  const signIn = useCallback((nextUser, { accessToken, refreshToken }) => {
    localStorage.setItem(KEYS.access, accessToken);
    localStorage.setItem(KEYS.refresh, refreshToken);
    localStorage.setItem(KEYS.user, JSON.stringify(nextUser));
    setUserState(nextUser);
  }, []);

  // Revokes the refresh token on the server too (best effort), so a copied token stops working at once.
  const signOut = useCallback(() => {
    const refreshToken = localStorage.getItem(KEYS.refresh);
    if (refreshToken) authAPI.logout(refreshToken).catch(() => {});
    clearBrowserSession();
    setUserState(null);
  }, []);

  // Restoring a session from localStorage must be verified against the backend, not trusted: a
  // stale or expired token would otherwise leave the UI "logged in" with an old role forever.
  // The saved user is shown immediately (no flash of "logged out"), then corrected. Expired access
  // tokens are refreshed by the API client; if the session is really dead, sign out.
  useEffect(() => {
    if (!localStorage.getItem(KEYS.access) || !localStorage.getItem(KEYS.user)) return;
    authAPI
      .getMe()
      .then(({ user: fresh }) => updateUser(fresh))
      .catch((err) => {
        // only a rejected session ends it; a network blip or a server restart must not log anyone out
        if (err.response?.status === 401) signOut();
      });
  }, [updateUser, signOut]);

  const value = useMemo(
    () => ({ user, isLoggedIn: user !== null, signIn, signOut, updateUser }),
    [user, signIn, signOut, updateUser]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
